import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Gamepad2, ChevronRight } from "lucide-react";
import { GAME_ICONS } from "@/components/GameTile";

interface PlayCtaFloatingProps {
  baseRoute: string;
  wheelEnabled: boolean;
  blackjackEnabled: boolean;
  slotEnabled: boolean;
}

/**
 * Floating "Jouer pour gagner" CTA shown when a digital menu takes over the page.
 * - 1 game enabled  -> direct navigation
 * - 2+ games enabled -> opens a bottom sheet to pick a game
 */
export const PlayCtaFloating = ({
  baseRoute,
  wheelEnabled,
  blackjackEnabled,
  slotEnabled,
}: PlayCtaFloatingProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const games: { key: "wheel" | "blackjack" | "slot"; label: string; route: string }[] = [];
  if (wheelEnabled) games.push({ key: "wheel", label: "Roue de la chance", route: `${baseRoute}/play/wheel` });
  if (blackjackEnabled) games.push({ key: "blackjack", label: "Blackjack express", route: `${baseRoute}/play/blackjack` });
  if (slotEnabled) games.push({ key: "slot", label: "Machine à sous", route: `${baseRoute}/play/slot` });

  if (games.length === 0) return null;

  const handleClick = () => {
    if (games.length === 1) navigate(games[0].route);
    else setOpen(true);
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-24 z-50 px-4 pointer-events-none">
        <Button
          onClick={handleClick}
          className="pointer-events-auto mx-auto flex h-14 w-full max-w-md items-center justify-center gap-2 rounded-full text-base font-bold shadow-brand pt-rise"
          size="lg"
        >
          <Gamepad2 className="h-5 w-5" />
          Jouer pour gagner
        </Button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl border-border">
          <SheetHeader>
            <SheetTitle className="font-display text-xl">Choisis ton jeu</SheetTitle>
          </SheetHeader>
          <div className="mx-auto max-w-md space-y-3 pt-4 pb-6">
            {games.map((g) => {
              const Icon = GAME_ICONS[g.key];
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(g.route);
                  }}
                  className="surface-tile pressable flex w-full items-center gap-3 rounded-2xl p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-display flex-1 text-base font-bold text-foreground">{g.label}</span>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PlayCtaFloating;
